import React, { JSX } from "react";
import { Box, Paper, Typography, useTheme } from "@mui/material";
import { WidgetResponseItem } from "system/widget/Widget";
import { QueryDrilldownDialog } from "components/dialogs/QueryDrilldown/QueryDrilldownDialog";
import { useTranslation } from "react-i18next";
import { Query } from "system/query/Query";
import { SubqueriesSettingsStateType } from "./SubqueriesSettings";
import SubqueriesVolume from "./Columns/SubqueriesVolume";
import SubqueriesSparkline from "./Columns/SubqueriesSparkline";
import SubqueriesKeywords from "./Columns/SubqueriesKeywords";
import SubqueriesSentiment from "./Columns/SubqueriesSentiment";

export interface SubqueryRowProps {
  rowIndex: number;
  rowResponse: WidgetResponseItem;
  rootResponse: WidgetResponseItem;
  widgetSettings: SubqueriesSettingsStateType;
}

export default function SubqueryRow(props: SubqueryRowProps): JSX.Element {
  const theme = useTheme();
  const { i18n } = useTranslation();

  const { rowResponse, rootResponse, widgetSettings } = props;

  const [valueDrilldownQuery, setValueDrilldownQuery] = React.useState<Query | undefined>();
  const [valueDrilldownTitle, setValueDrilldownTitle] = React.useState<string | undefined>();

  const mediumBreakpoint = "400px",
        largeBreakpoint = "700px";

  const activeQueryIDs = rowResponse?.originatingRequest?.activeQueryIds;

  let visibleColumns = 0;
  if (!widgetSettings?.volumeColumnHidden) visibleColumns++;
  if (!widgetSettings?.sparklineColumnHidden) visibleColumns++;
  if (!widgetSettings?.sentimentColumnHidden) visibleColumns++;
  if (!widgetSettings?.keywordColumnHidden) visibleColumns++;

  const largeGridTemplateColumns = '200px ' + '2fr '.repeat(visibleColumns).trim();

  return (
    <Paper
        sx={{
          m: 1, mx: 1, p: 1, py: 0.5,
          minHeight: "60px",
          position: "relative",
          backgroundColor: theme.palette.background.paper,
          containerType: 'inline-size',
          containerName: 'subqueryrow'
        }}>
      <Box sx={{ alignItems: "center", display: "grid", gap: 2,
        [`@container subqueryrow (width < ${mediumBreakpoint})`]: {
          gridTemplateColumns: '1fr',
        },
        [`@container subqueryrow (width >= ${mediumBreakpoint}) and (width < ${largeBreakpoint})`]: {
          gridTemplateColumns: '1fr 1fr',
        },
        [`@container subqueryrow (width >= ${largeBreakpoint})`]: {
          gridTemplateColumns: largeGridTemplateColumns,
        },

       }}>
        <Box sx={{
          [`@container subqueryrow (width < ${mediumBreakpoint})`]: {
            gridColumn: "1",
            textAlign: "center",
          },
          [`@container subqueryrow (width >= ${mediumBreakpoint}) and (width < ${largeBreakpoint})`]: {
            gridColumn: "1 / span 2",
            textAlign: "center",
          },
          [`@container subqueryrow (width >= ${largeBreakpoint})`]: {
            gridColumn: 1,
          },
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          minHeight: "60px",
          display: "flex",
          alignItems: "center"
        }}>
          <Typography variant="h4" sx={{
              color: theme.palette.text.secondary,
            }}>
            {rowResponse.queryDisplayTitle || `Query: ${props.rowIndex}`}
          </Typography>
        </Box>
        {!widgetSettings?.volumeColumnHidden && (
          <SubqueriesVolume
            rowResponse={rowResponse}
            rootResponse={rootResponse}
            widgetSettings={widgetSettings}
          />
        )}

        {!widgetSettings?.sparklineColumnHidden && (
          <SubqueriesSparkline
            rowResponse={rowResponse}
            rootResponse={rootResponse}
            setValueDrilldownQuery={setValueDrilldownQuery}
            setValueDrilldownTitle={setValueDrilldownTitle}
          />
        )}

        {!widgetSettings?.sentimentColumnHidden && (
          <SubqueriesSentiment
          rowResponse={rowResponse}
          rootResponse={rootResponse}
          setValueDrilldownQuery={setValueDrilldownQuery}
          setValueDrilldownTitle={setValueDrilldownTitle}
          />
        )}
        {!widgetSettings?.keywordColumnHidden && (
          <SubqueriesKeywords
          rowResponse={rowResponse}
          rootResponse={rootResponse}
          setValueDrilldownQuery={setValueDrilldownQuery}
          setValueDrilldownTitle={setValueDrilldownTitle}
          />
        )}
      </Box>
      {valueDrilldownQuery && (
        <QueryDrilldownDialog
          title={ `${i18n.t("widget-types.subqueries.subquery")} ${rowResponse.queryDisplayTitle} - ${valueDrilldownTitle}`}
          onClose={() => setValueDrilldownQuery(undefined)}
          drilldownQuery={valueDrilldownQuery}
          activeQueryIds={activeQueryIDs}
        />
      )}
    </Paper>
  );
}